import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  getAppointmentsOverview,
  getAppointmentsPeakHours
} from '../../services/analytics.service';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

/* ------------------------------------
   Spinner Overlay (Reusable)
------------------------------------ */
const ChartLoader = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
    <div className="h-10 w-10 rounded-full border-4 border-gray-200 border-t-blue-500 animate-spin" />
  </div>
);

const AppointmentsInsights = ({ filters }) => {
  const [overviewData, setOverviewData] = useState([]);
  const [peakHoursData, setPeakHoursData] = useState([]);

  const [loading, setLoading] = useState(false);
  const ChartEmptyState = ({ message }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 z-10">
    
    <p className="text-sm">{message}</p>
  </div>
);



  const requestIdRef = useRef(0);

  /* ------------------------------------
     Fetch Data (Parallel + Safe)
  ------------------------------------ */
  useEffect(() => {
  let params;

  if (filters?.from && filters?.to) {
    params = { from: filters.from, to: filters.to };
  } else if (filters?.range) {
    params = { range: filters.range };
  } else {
    return;
  }

  const controller = new AbortController();

  const fetchData = async () => {
    try {
      setLoading(true);

      const [
        overviewResponse,
        peakHoursResponse
      ] = await Promise.all([
        getAppointmentsOverview(params, controller.signal),
        getAppointmentsPeakHours(params, controller.signal)
      ]);
      
      const raw = overviewResponse?.data || [];
      const bucket = overviewResponse?.bucket || 'day';

      const normalized = raw.map(row => {
        if (bucket === 'day') {
          return {
            ...row,
            DateLabel: new Date(row.Date).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short'
            })
          };
        }

        return row;
      });

      const hours = Array.from({ length: 24 }, (_, i) => i);
      const mapped = hours.map(hour => {
        const rec = peakHoursResponse?.find(h => h.Hour === hour);
        return {
          Hour: hour,
          Appointments: rec?.Appointments || 0
        };
      });
      if (controller.signal.aborted) return;

      setOverviewData(normalized);
      setPeakHoursData(mapped);
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('AppointmentsInsights request aborted');
        return;
      }

      console.error('AppointmentsInsights load failed', err);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  fetchData();

  return () => {
    controller.abort();
  };
}, [filters?.range, filters?.from, filters?.to]);
  

  /* ------------------------------------
     Memoized Data (Future-safe)
  ------------------------------------ */
  const overviewChartData = useMemo(
    () => overviewData || [],
    [overviewData]
  );

  const peakChartData = useMemo(
    () => peakHoursData || [],
    [peakHoursData]
  );
  const hasOverviewData = useMemo(() => {
  return overviewChartData.some(
    d => (d.Booked || d.Completed || d.Cancelled || d.NoShow)
  );
}, [overviewChartData]);

const hasPeakData = useMemo(() => {
  return peakChartData.some(d => d.Appointments > 0);
}, [peakChartData]);
const shouldHideXAxisTicks = useMemo(() => {
  if (!filters?.from || !filters?.to) return false;

  const from = new Date(filters.from);
  const to = new Date(filters.to);

  const diffDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24));

  return diffDays > 15;
}, [filters?.from, filters?.to]);

  return (
    <div className="appointments-insights space-y-10">

      {/* ==============================
         Appointments Over Time
      ============================== */}
      <div className="card bg-white shadow-lg rounded-xl p-6 relative">
        <h2 className="text-xl font-semibold mb-4">📊 Appointments Over Time</h2>

        <div className="relative" style={{ height: 300 }}>
          {loading && <ChartLoader />}

          {!loading && !hasOverviewData && (
            <ChartEmptyState message="No appointments found for this period" />
          )}

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={overviewChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
  dataKey="DateLabel"
  tickFormatter={(value) =>
    shouldHideXAxisTicks ? '' : value
  }
  axisLine={true}
  tickLine={!shouldHideXAxisTicks}
/>
              <YAxis />
              <Tooltip labelFormatter={(label) => label || ''} />
              <Legend />
              <Bar dataKey="Booked" fill="#3b82f6" />
              <Bar dataKey="Completed" fill="#10b981" />
              <Bar dataKey="Cancelled" fill="#ef4444" />
              <Bar dataKey="NoShow" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </div>


        {/* <p className="text-sm text-gray-500 mt-2">
          Aggregation: <strong>{bucketType}</strong>
        </p> */}
      </div>

      {/* ==============================
         Peak Hours
      ============================== */}
      <div className="card bg-white shadow-lg rounded-xl p-6 relative">
        <h2 className="text-xl font-semibold mb-4">⏰ Peak Hours</h2>

        <div className="relative" style={{ height: 300 }}>
          {loading && <ChartLoader />}

          {!loading && !hasPeakData && (
            <ChartEmptyState message="No peak-hour activity found" />
          )}

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={peakChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Hour" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="Appointments" fill="#6366f1" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

    </div>
  );
};

export default AppointmentsInsights;
